/**
 * Line-level helpers for Goods Receipting expanded product rows.
 */
import {
  OMS_VAT_TREATMENT,
  buildVatFieldsForPersist,
  omsVatTreatmentImpliesHasVat,
  resolveLineVatTreatment,
  resolveOmsVatTreatment,
} from 'src/utils/vatTreatment'

/**
 * Same title resolution as ProductColumns Title cell.
 * Title may live only on `product.title` when not denormalised onto the order.
 */
export function resolveReceiptingLineTitle(record = {}) {
  if (record?.status === 'Rejected by supplier') {
    return record.title || record.product?.title || ''
  }
  return (
    record?.receipting?.updatedTitle ||
    record?.title ||
    record?.product?.title ||
    ''
  )
}

/**
 * Effective VAT for display (order.hasVat true, else product.hasVat).
 * Prefer vatTreatment when present (standard ⇒ true).
 */
export function resolveReceiptingLineHasVat(record = {}) {
  const treatment = resolveLineVatTreatment(record)
  return omsVatTreatmentImpliesHasVat(treatment)
}

/** Persisted order VAT treatment (with legacy hasVat fallback). */
export function getSavedOrderVatTreatment(record = {}) {
  return resolveOmsVatTreatment({
    vatTreatment: record?.vatTreatment,
    hasVat: record?.hasVat === true ? true : record?.hasVat === false ? false : undefined,
    product: record?.product,
  })
}

/** Toggle/select display: local draft wins, else effective treatment. */
export function getOrderVatTreatmentDisplay(record = {}, draftValue) {
  if (typeof draftValue === 'string' && draftValue) {
    return resolveOmsVatTreatment({ vatTreatment: draftValue })
  }
  if (typeof draftValue === 'boolean') {
    return draftValue
      ? OMS_VAT_TREATMENT.STANDARD
      : OMS_VAT_TREATMENT.ZERO_RATED
  }
  return getSavedOrderVatTreatment(record)
}

export function isOrderVatTreatmentDraftDirty(record = {}, draftValue) {
  if (draftValue == null || draftValue === '') return false
  const draft = getOrderVatTreatmentDisplay(record, draftValue)
  return draft !== getSavedOrderVatTreatment(record)
}

/** Persisted order.hasVat only (not product fallback). */
export function getSavedOrderHasVat(record = {}) {
  if (record?.vatTreatment) {
    return omsVatTreatmentImpliesHasVat(record.vatTreatment)
  }
  return record?.hasVat === true
}

/** Toggle display: local draft wins, else effective resolved VAT. */
export function getOrderHasVatDisplay(record = {}, draftValue) {
  if (typeof draftValue === 'boolean') {
    return draftValue
  }
  if (typeof draftValue === 'string' && draftValue) {
    return omsVatTreatmentImpliesHasVat(draftValue)
  }
  return resolveReceiptingLineHasVat(record)
}

export function isOrderHasVatDraftDirty(record = {}, draftValue) {
  if (typeof draftValue === 'string' && draftValue) {
    return isOrderVatTreatmentDraftDirty(record, draftValue)
  }
  if (typeof draftValue !== 'boolean') {
    return false
  }
  return draftValue !== getSavedOrderHasVat(record)
}

export function buildOrderVatUpdatePayload(record = {}, draftValue) {
  const treatment = getOrderVatTreatmentDisplay(record, draftValue)
  return {
    orderId: record?._id,
    ...buildVatFieldsForPersist({
      vatTreatment: treatment,
      priceIncludesVat:
        treatment === OMS_VAT_TREATMENT.STANDARD
          ? getSavedOrderPriceIncludesVat(record)
          : false,
    }),
  }
}

/** Persisted order.priceIncludesVat only. */
export function getSavedOrderPriceIncludesVat(record = {}) {
  return Boolean(record?.priceIncludesVat)
}

/** Toggle display: local draft wins, else saved value. */
export function getOrderPriceIncludesVatDisplay(record = {}, draftValue) {
  if (typeof draftValue === 'boolean') {
    return draftValue
  }
  return getSavedOrderPriceIncludesVat(record)
}

export function isOrderPriceIncludesVatDraftDirty(record = {}, draftValue) {
  if (typeof draftValue !== 'boolean') {
    return false
  }
  return draftValue !== getSavedOrderPriceIncludesVat(record)
}

export function isReceiptingVatDraftDirty(
  record = {},
  hasVatDraft,
  priceIncludesVatDraft,
) {
  return (
    isOrderHasVatDraftDirty(record, hasVatDraft) ||
    isOrderPriceIncludesVatDraftDirty(record, priceIncludesVatDraft)
  )
}

export function isManualPlacedOrderLine(record = {}) {
  const placedVia = String(record?.placedVia || '').toLowerCase()
  return (
    placedVia.includes('manual') ||
    placedVia.includes('dashboard') ||
    placedVia.includes('phone')
  )
}

export function isSupplierRejectedLine(record = {}) {
  return record?.supplierConfirmed === false
}

/**
 * Show "Waiting on Supplier Approval" only when the line is not yet
 * supplier-confirmed. Never treat a missing denormalised `title` as waiting —
 * that false-positive blocked Report Error on Confirmed orders (e.g. ABC Vic Test).
 */
export function shouldShowWaitingOnSupplierApproval(record = {}) {
  if (record?.supplierConfirmed === true) {
    return false
  }

  if (isSupplierRejectedLine(record)) {
    return false
  }

  // Receipting already started on this line — show Report Error / actions.
  if (record?.status != null && String(record.status).trim() !== '') {
    return false
  }

  return true
}
